import { OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

import { UtilsService } from '../../services/utils.service';
import { WalkthroughService } from '../../services/walkthrough.service';
import { ObjectActionsService } from '../../services/object-actions.service';
import { ObjectCreatorService } from '../../services/object-creator.service';

import { DataObject, Workspace } from '../../models/core.models';
import { NomObjectClass, SortDirection } from '../../constants';

import {
  setHomeTab,
  setHomeSortingField,
  setHomeFilterString,
  setHomeObjectsLifeCycle,
  setHomeSortingDirection,
  fetchHomeViewConfig,
  toggleHomeViewConfigOption,
} from '../../actions/home.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from '../../actions/selected.actons';
import { objectChanged } from '../../actions/core.actions';

import {
  selectCoreLoading,
  selectRecentObjects,
  selectHomeWorkspaces,
  selectSelectedObjects,
  selectUser,
  selectHomeFilterString,
  selectHomeViewOptions,
  selectHomeObjectsLifeCycle,
  selectHomeSortingField,
  selectHomeSortingDirection,
} from '../../reducers';
import { AppState } from 'src/app/reducers';

import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  take,
  tap,
} from 'rxjs';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ObjectContextSettings } from '../../services/object.service';
import { PropertyViewConfig } from '../../services/feed-settings.service';

export type HomeTab = 'active' | 'recent' | 'archived' | 'deleted';

/** A row in a projected toolbar dropdown panel (view options / sort). */
interface ToolbarMenuItem {
  text?: string;
  icon?: IconProp;
  value?: unknown;
  label?: string;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
}

/** A run of consecutive same-`radioGroup` rows, or a single toggle row. */
interface ToolbarMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: unknown;
  items: ToolbarMenuItem[];
}

/** A command row in the "apply to selected" dropdown (from ObjectActionsService). */
interface ApplyAction {
  text: string;
  icon?: IconProp;
  // eslint-disable-next-line @typescript-eslint/ban-types
  action?: Function; // matches ObjectActionsService.getButtonForAction's action
}
@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HomeComponent implements OnInit {
  currentTab: HomeTab;
  filterString: string;
  workspaces: Array<Workspace>;
  viewConfig: PropertyViewConfig[] = [];

  // * Toolbar state (migrated from HomeActionsService)
  selectAllChecked = false;
  lifecycleStatus: number = 1;
  sortField = 'title';
  sortDirection = 'asc';
  viewItems: ToolbarMenuItem[] = [];
  viewGroups: ToolbarMenuGroup[] = [];
  applyActions: ApplyAction[] = [];
  applyCount = 0;

  private readonly statusLabels: Record<number, string> = {
    1: 'home-actions.button.active',
    3: 'home-actions.button.archived',
    4: 'home-actions.button.deleted',
  };
  private readonly statusIcons: Record<number, IconProp> = {
    1: ['fas', 'check'],
    3: ['fas', 'box-archive'],
    4: ['fas', 'trash'],
  };

  /** Sort trigger's leading icon reflects direction; the trailing icon reflects the field. */
  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'sort-amount-up']
      : ['fas', 'sort-amount-down'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortField === 'time_created' ||
      this.sortField === 'time_modified'
      ? ['fas', 'clock']
      : ['fas', 'text-height'];
  }
  get statusLabel(): string {
    return this.translateService.instant(
      this.statusLabels[this.lifecycleStatus] ?? this.statusLabels[1],
    );
  }
  get statusIcon(): IconProp {
    return this.statusIcons[this.lifecycleStatus] ?? this.statusIcons[1];
  }
  get applyLabel(): string {
    return `${this.translateService.instant('home-actions.button.apply-to-selected')} (${this.applyCount})`;
  }
  get sortLabel(): string {
    return this.translateService.instant('home-actions.button.sort');
  }
  get viewLabel(): string {
    return this.translateService.instant('workspace-actions.button.view');
  }

  isLoading = true;
  tabContent: 'workspaces' | 'recent' = 'workspaces';

  loading$: Observable<boolean>;
  workspaces$: Observable<Array<Workspace>>;
  recentObjects$: Observable<Array<DataObject>>;

  private destroyRef = inject(DestroyRef);

  constructor(
    protected store: Store<AppState>,
    protected route: ActivatedRoute,
    protected utilService: UtilsService,
    protected objectActionService: ObjectActionsService,
    protected objectCreatorService: ObjectCreatorService,
    protected translateService: TranslateService,
    protected walktrhoughService: WalkthroughService,
  ) {
    this.workspaces$ = this.store.select(selectHomeWorkspaces);
    this.workspaces$
      .pipe(
        // side-effect: update the local workspaces array
        tap((workspaces) => (this.workspaces = workspaces)),
        // automatically unsubscribe when the component is destroyed
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.recentObjects$ = this.store.select(selectRecentObjects);
    this.loading$ = this.store.select(selectCoreLoading);
  }

  public ngOnInit(): void {
    this.isLoading = false;
    this.store.dispatch(objectChanged({ object: null }));

    this.route.params
      .pipe(
        tap((params) => {
          // Set default tab if none is provided
          this.currentTab = params['tab'] ? params['tab'] : 'active'; // Replace 'defaultTab' with your actual default tab value
          this.store.dispatch(setHomeTab({ tab: this.currentTab }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap((user) => {
          this.store.dispatch(fetchHomeViewConfig({ userId: user.id }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectHomeViewOptions)
      .pipe(
        tap((config) => {
          this.viewConfig = config;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setupToolbar();
  }

  public setupToolbar(): void {
    this.store
      .pipe(
        select(selectHomeObjectsLifeCycle),
        distinctUntilChanged(),
        tap(
          (status) =>
            (this.lifecycleStatus = this.statusLabels[status] ? status : 1),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([
      this.store.pipe(
        select(selectHomeSortingDirection),
        distinctUntilChanged(),
      ),
      this.store.pipe(select(selectHomeSortingField), distinctUntilChanged()),
    ])
      .pipe(
        tap(([dir, field]) => {
          this.sortDirection = dir;
          this.sortField = field;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectHomeViewOptions)
      .pipe(
        tap((config) => {
          this.viewItems = (config ?? []).map((c) => ({
            text: c.label,
            label: c.label,
            icon: c.icon,
            value: c.path,
            isToggle: !c.isRadio,
            isRadio: c.isRadio,
            isSelected: c.selected,
            radioGroup: c.radioGroup,
          }));
          this.viewGroups = this.groupViewItems(this.viewItems);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectSelectedObjects),
        tap((selectedObjects) => {
          this.applyCount = selectedObjects.size;
          this.updateApplyActions(selectedObjects);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectHomeFilterString)
      .pipe(
        tap((filterString) => (this.filterString = filterString)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateApplyActions(selectedObjects: Map<string, DataObject>): void {
    let applicable: string[] = [];
    selectedObjects.forEach((object) => {
      let classId = object.object_class_id;
      const typeId = object.object_type_id || null;
      // widget placeholders set object_class_id/object_id to the same value
      if (object['widget_type_id'] !== undefined) {
        classId = NomObjectClass.WIDGET;
      }
      const actions =
        this.objectActionService.objectActions[classId]?.[
          object.lifecycle_status
        ]?.[typeId] ?? null;
      if (actions) {
        const enabled = Object.keys(actions).filter((a) => actions[a] === true);
        applicable =
          applicable.length === 0
            ? enabled
            : applicable.filter((a) => enabled.includes(a));
      }
    });

    this.applyActions = applicable.map((action) => {
      const button = this.objectActionService.getButtonForAction(action);
      return {
        text: button.label,
        icon: button.leadingIcon,
        action: button.action,
      };
    });
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.selectAllWorkspaces(selected);
  }

  onStatusChange(status: number): void {
    this.lifecycleStatus = status;
    this.store.dispatch(
      setHomeObjectsLifeCycle({ homeObjectsLifeCycle: status }),
    );
  }

  onSortFieldChange(sortingField: string): void {
    this.sortField = sortingField;
    this.store.dispatch(setHomeSortingField({ sortingField }));
  }

  onSortDirectionChange(sortingDirection: SortDirection): void {
    this.sortDirection = sortingDirection;
    this.store.dispatch(setHomeSortingDirection({ sortingDirection }));
  }

  onViewToggle(item: ToolbarMenuItem): void {
    this.store.dispatch(toggleHomeViewConfigOption({ optionName: item.label }));
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: ToolbarMenuItem[]): ToolbarMenuGroup[] {
    const groups: ToolbarMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ToolbarMenuGroup, value: unknown): void {
    const item = group.items.find((i) => i.value === value);
    if (item) {
      this.onViewToggle(item);
    }
  }

  handleSearchStringChange(query: string) {
    // Use the debounced function instead of the original search function
    if (typeof query == 'string') {
      this.filterString = query;
      this.store.dispatch(
        setHomeFilterString({ filterString: this.filterString }),
      );
    }
  }

  createNewWorkspace() {
    this.objectCreatorService.createNewWorkspace();
  }

  selectAllWorkspaces(selected: boolean) {
    if (selected) {
      this.store.dispatch(selectObjects({ objects: this.workspaces }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  trackById(index: number, workspace: any): any {
    return workspace.id;
  }
}
